import { MoveToInbox, QueryBuilder, Sensors, Wifi } from "@mui/icons-material";
import {
  Alert,
  alpha,
  Box,
  CircularProgress,
  darken,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { ReactNode } from "react";

export type ConnectivityTestIcon = "PING" | "SNMP" | "PACOTES";

export interface ConnectivityTestData {
  title: string;
  type: ConnectivityTestIcon;
  status?: boolean;
  message?: string;
  timerExec?: string;
}

export interface CardConnectivityProps {
  testData: ConnectivityTestData;
  loading: boolean;
}

const ICONS: Record<ConnectivityTestIcon, ReactNode> = {
  PING: <Wifi />,
  SNMP: <Sensors />,
  PACOTES: <MoveToInbox />,
};

export default function CardConnectivity(props: CardConnectivityProps) {
  const { testData, loading } = props;
  const isWaiting = !loading && testData?.status === undefined;

  const theme = useTheme();

  const icon = ICONS[testData?.type] ?? <Wifi />;

  const color =
    theme.palette.mode === "dark"
      ? testData?.status === true
        ? theme.palette.sucess.main
        : theme.palette.error.main
      : testData?.status === true
        ? darken(theme.palette.sucess.main, 0.2)
        : darken(theme.palette.error.main, 0.2);

  if (loading || isWaiting) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 2,
          alignItems: "center",
          justifyContent: "flex-start",
          borderRadius: 2,
          backgroundColor: theme.palette.background.card,
          padding: 2,
        }}
      >
        <Stack
          sx={{
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            p: 0.5,
            borderRadius: 1.5,
            color: theme.palette.primary.main,
          }}
        >
          {icon}
        </Stack>
        <Stack
          direction="row"
          sx={{
            flexGrow: 1,
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Stack>
            <Typography variant="subtitle2">{testData?.title}</Typography>
            <Typography variant="body2" color="text.secondary">
              {loading ? "Executando..." : "Aguardando..."}
            </Typography>
          </Stack>
          {loading && <CircularProgress size={20} />}
        </Stack>
      </Box>
    );
  }

  return (
    <Alert
      icon={
        <Stack
          sx={{
            backgroundColor: alpha(color, 0.2),
            padding: 0.5,
            borderRadius: 1.5,
            maxHeight: "fit-content",
            maxWidth: "fit-content",
            color: color,
          }}
        >
          {icon}
        </Stack>
      }
      severity={testData?.status === true ? "success" : "error"}
      sx={{
        width: "100%",
        "& .MuiAlert-message": {
          width: "100%",
        },
        alignItems: "center",
        borderRadius: 2,
        color: color,
        borderLeft: `3px solid ${alpha(color, 0.8)}`,
        backgroundColor: theme.palette.background.card,
      }}
    >
      <Stack
        direction="row"
        sx={{
          width: "100%",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Stack>
          <Typography color="text.primary" variant="subtitle2">
            {testData?.title}
          </Typography>
          <Tooltip title={testData?.message ?? ""}>
            <Typography
              variant="body2"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: { xs: 150, sm: 300 },
              }}
            >
              {testData?.message}
            </Typography>
          </Tooltip>
        </Stack>
        {testData?.timerExec && (
          <Stack
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 0.5,
              alignItems: "center",
            }}
          >
            <QueryBuilder fontSize="inherit" sx={{ color: "text.secondary" }} />
            <Typography variant="caption" color="text.secondary">
              {testData?.timerExec}
            </Typography>
          </Stack>
        )}
      </Stack>
    </Alert>
  );
}
